import { DeviceStatusInfo } from "@fairfleet/geotab";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeotabClient } from "@/components/GeotabClientContext";
import i18n from "@/i18n";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";

export function AddressCell(props: GridRenderCellParams<DeviceStatusOverviewData>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deviceId = props.row.device?.id;
  const { activeSessionId } = useGeotabClient();
  // Query cached data, because this is updated when opening detailed view, so the detailed data is synced with this address data
  const cachedData = queryClient.getQueryData<DeviceStatusInfo>([
    "DeviceStatusInfo",
    activeSessionId,
    deviceId,
  ]);
  const isDriving = cachedData?.isDriving ?? props.row.statusData?.isDriving;
  const statusSince =
    cachedData?.currentStateDuration ?? props.row.statusData?.currentStateDuration;
  // const currentStateDuration = cachedData?.currentStateDuration ?? props.row.statusData?.currentStateDuration;
  const dateTime = cachedData?.dateTime ?? props.row.statusData?.dateTime;
  //   const statusSince = currentStatusDuration ? currentStatusDuration : currentStateDuration;
  const dateUtc = dateTime ? new Date(dateTime) : null;
  const duration =
    statusSince && dateUtc ? formatDuration(calculateDuration(statusSince, dateUtc)) : undefined;
  return (
    <Tooltip title={props.row.address ?? ""}>
      <ListItemText
        primary={props.value !== "" ? (props.value ?? "---") : "---"}
        secondary={
          (isDriving ? t("deviceStatus.driving") : t("deviceStatus.notDriving")) +
          " " +
          (statusSince && duration ? t("deviceStatus.since") + " " + duration : "")
        }
      />
    </Tooltip>
  );
}

export function calculateDuration(currentStatusDuration: string, dateTime: Date | null) {
  if (!dateTime) return 0;
  const dateTimeDate = new Date(dateTime);
  const currentDate = new Date();
  let diffMs = currentDate.getTime() - dateTimeDate.getTime();

  let durationMs = 0;
  if (currentStatusDuration.includes(".")) {
    // Format "D.HH:MM:SS"
    const [days, time] = currentStatusDuration.split(".");
    const [hours, minutes, seconds] = time.split(":").map(Number);
    durationMs = parseInt(days) * 24 * 3600000 + hours * 3600000 + minutes * 60000 + seconds * 1000;
  } else {
    // Format "HH:MM:SS"
    const [hours, minutes, seconds] = currentStatusDuration.split(":").map(Number);
    durationMs = hours * 3600000 + minutes * 60000 + seconds * 1000;
  }

  diffMs += durationMs;
  return diffMs;
}

function formatDuration(diffMs: number) {
  const days = Math.floor(diffMs / (24 * 3600000));
  diffMs -= days * (24 * 3600000);
  const hours = Math.floor(diffMs / 3600000);
  diffMs -= hours * 3600000;
  const minutes = Math.floor(diffMs / 60000);
  diffMs -= minutes * 60000;
  const seconds = Math.floor(diffMs / 1000);

  let result = "";
  if (days > 0) result += `${days}${i18n.t("abrevtime.days")} `;
  if (hours > 0 || result.length > 0) result += `${hours}${i18n.t("abrevtime.hours")} `;
  result += `${minutes}${i18n.t("abrevtime.minutes")} `;
  if (seconds > 0) result += `${seconds}${i18n.t("abrevtime.seconds")}`;

  return result.trim();
}
